import { Head, Link, router } from "@inertiajs/react"
import {
  Plus,
  List,
  Grid3x3,
  Trash2,
  Eye,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useState, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { samplesPath, editSamplePath, samplePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Tasting {
  id: number
  title: string
}

interface Sample {
  id: number
  name: string
  description: string
  origin: string
  land: string
  producer: string
  process: string
  variety: string
  picture: string
  picture_url: string | null
  tasting?: Tasting
}

interface Props {
  samples: Sample[]
  total_count: number
  page: number
  per_page: number
  search: string
}

export default function StandaloneSamplesIndex({
  samples,
  total_count,
  page,
  per_page,
  search: initialSearch,
}: Props) {
  const { t } = useTranslation()
  const [selectedSamples, setSelectedSamples] = useState<number[]>([])

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("samples.breadcrumb"),
      href: samplesPath(),
    },
  ]
  const [viewMode, setViewMode] = useState<"list" | "grid">("list")
  const [sortBy, setSortBy] = useState<string>("date")
  const [searchQuery, setSearchQuery] = useState(initialSearch || "")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [sampleToDelete, setSampleToDelete] = useState<{
    id: number
    name: string
  } | null>(null)

  const totalPages = Math.ceil(total_count / per_page)

  const navigateToPage = useCallback(
    (newPage: number, newSearch?: string) => {
      const params: Record<string, string> = { page: String(newPage) }
      const s = newSearch !== undefined ? newSearch : searchQuery
      if (s) params.search = s
      router.get(samplesPath(), params, { preserveState: true })
    },
    [searchQuery],
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigateToPage(1, searchQuery)
  }

  const clearSearch = () => {
    setSearchQuery("")
    navigateToPage(1, "")
  }

  const sortedSamples = useMemo(() => {
    return [...samples].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.name || "").localeCompare(b.name || "")
        case "origin":
          return (a.origin || "").localeCompare(b.origin || "")
        case "date":
        default:
          return b.id - a.id
      }
    })
  }, [samples, sortBy])

  const openDeleteModal = (id: number, name: string) => {
    setSampleToDelete({ id, name })
    setDeleteModalOpen(true)
  }

  const handleDelete = () => {
    if (sampleToDelete) {
      router.delete(samplePath(sampleToDelete.id))
      setDeleteModalOpen(false)
      setSampleToDelete(null)
    }
  }

  const toggleSelectAll = () => {
    if (selectedSamples.length === samples.length) {
      setSelectedSamples([])
    } else {
      setSelectedSamples(samples.map((s) => s.id))
    }
  }

  const toggleSelect = (id: number) => {
    if (selectedSamples.includes(id)) {
      setSelectedSamples(selectedSamples.filter((sid) => sid !== id))
    } else {
      setSelectedSamples([...selectedSamples, id])
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("samples.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <h1 className="text-3xl font-bold">{t("samples.title")}</h1>

        <Card>
          <CardContent className="p-6">
            {/* Search - show if total samples > 10 or there's an active search */}
            {(total_count > 10 || initialSearch) && (
              <form
                onSubmit={handleSearch}
                className="mb-4 flex items-center gap-2"
              >
                <div className="relative max-w-sm flex-1">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("samples.index.search_placeholder")}
                    className="pl-9"
                  />
                </div>
                <Button type="submit" variant="outline" size="sm">
                  {t("samples.index.search_button")}
                </Button>
                {initialSearch && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSearch}
                  >
                    {t("samples.index.clear_button")}
                  </Button>
                )}
              </form>
            )}

            {/* Toolbar */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t("samples.index.sort_by")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">
                      {t("samples.index.sort_date")}
                    </SelectItem>
                    <SelectItem value="name">
                      {t("samples.index.sort_name")}
                    </SelectItem>
                    <SelectItem value="origin">
                      {t("samples.index.sort_origin")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className={
                    viewMode === "list" ? "bg-primary hover:bg-primary/90" : ""
                  }
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className={
                    viewMode === "grid" ? "bg-primary hover:bg-primary/90" : ""
                  }
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Link href="/samples/new">
                  <Button
                    variant="outline"
                    size="icon"
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {samples.length === 0 ? (
              <div className="text-muted-foreground py-12 text-center">
                {t("samples.index.no_samples_message")}
              </div>
            ) : viewMode === "list" ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-b">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedSamples.length === samples.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>{t("samples.index.table_headers.id")}</TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.name")}
                    </TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.origin")}
                    </TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.producer")}
                    </TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.process")}
                    </TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.variety")}
                    </TableHead>
                    <TableHead>
                      {t("samples.index.table_headers.action")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSamples.map((sample) => (
                    <TableRow key={sample.id} className="border-b">
                      <TableCell>
                        <Checkbox
                          checked={selectedSamples.includes(sample.id)}
                          onCheckedChange={() => toggleSelect(sample.id)}
                        />
                      </TableCell>
                      <TableCell className="text-primary font-medium">
                        #{String(sample.id).padStart(3, "0")}
                      </TableCell>
                      <TableCell>{sample.name || "-"}</TableCell>
                      <TableCell>{sample.origin || "-"}</TableCell>
                      <TableCell>{sample.producer || "-"}</TableCell>
                      <TableCell>
                        {t(`samples.index.process_labels.${sample.process}`, {
                          defaultValue: sample.process,
                        }) || "-"}
                      </TableCell>
                      <TableCell>
                        {t(`samples.index.variety_labels.${sample.variety}`, {
                          defaultValue: sample.variety,
                        }) || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Link href={samplePath(sample.id)}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={t("samples.actions.view")}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link href={editSamplePath(sample.id)}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={t("samples.actions.edit")}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive h-8 w-8"
                            onClick={() =>
                              openDeleteModal(sample.id, sample.name)
                            }
                            title={t("samples.actions.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sortedSamples.map((sample) => (
                  <Link key={sample.id} href={samplePath(sample.id)}>
                    <Card className="hover:border-primary/50 h-full cursor-pointer transition-colors">
                      <CardContent className="p-4">
                        {sample.picture_url ? (
                          <img
                            src={sample.picture_url}
                            alt={sample.name}
                            className="mb-3 h-40 w-full rounded-lg object-cover"
                          />
                        ) : (
                          <div className="bg-muted mb-3 flex h-40 w-full items-center justify-center rounded-lg">
                            <span className="text-muted-foreground text-sm">
                              {t("samples.index.no_image")}
                            </span>
                          </div>
                        )}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="truncate font-semibold">
                              {sample.name}
                            </h3>
                            <span className="text-primary text-xs font-medium">
                              #{String(sample.id).padStart(3, "0")}
                            </span>
                          </div>
                          <p className="text-muted-foreground text-sm">
                            {sample.origin || "-"}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {sample.process && (
                              <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                                {t(
                                  `samples.index.process_labels.${sample.process}`,
                                  { defaultValue: sample.process },
                                )}
                              </span>
                            )}
                            {sample.variety && (
                              <span className="bg-accent text-accent-foreground inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                                {t(
                                  `samples.index.variety_labels.${sample.variety}`,
                                  { defaultValue: sample.variety },
                                )}
                              </span>
                            )}
                          </div>
                          {sample.producer && (
                            <p className="text-muted-foreground text-xs">
                              {t("samples.index.producer_label")}:{" "}
                              {sample.producer}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-muted-foreground text-sm">
                  {t("samples.index.pagination.showing")}{" "}
                  {(page - 1) * per_page + 1}-
                  {Math.min(page * per_page, total_count)}{" "}
                  {t("samples.index.pagination.of")} {total_count}{" "}
                  {t("samples.index.pagination.samples")}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page <= 1}
                    onClick={() => navigateToPage(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <Button
                        key={p}
                        variant={p === page ? "default" : "outline"}
                        size="icon"
                        className={`h-8 w-8 ${p === page ? "bg-primary hover:bg-primary/90" : ""}`}
                        onClick={() => navigateToPage(p)}
                      >
                        {p}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page >= totalPages}
                    onClick={() => navigateToPage(page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("samples.index.delete_modal.title")}</DialogTitle>
              <DialogDescription>
                {t("samples.index.delete_modal.description")}{" "}
                {sampleToDelete?.name
                  ? `"${sampleToDelete.name}"`
                  : t("samples.index.delete_modal.description_fallback")}
                {t("samples.index.delete_modal.description_suffix")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModalOpen(false)}
              >
                {t("samples.index.delete_modal.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t("samples.index.delete_modal.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  )
}
